/**
 * Skills management client (O-1162 parity port from dashboard-legacy/src/api/skills.ts).
 *
 * Covers the admin-facing CRUD + presigned-upload + deploy/undeploy
 * surface for tenant-scoped Skills (the .skill archives shipped to
 * endpoints to drive coding-agent skill files).
 *
 * Wire conventions (per grpc-gateway):
 *   - IDs (skill_id, version_id, endpoint_id) are base64 strings end-to-end;
 *     no UUID↔bytes helper here, by design (see root AGENTS.md).
 *   - `bytes` fields come back as base64 strings; the dashboard never
 *     decodes them.
 *   - int64 fields (e.g. blob_size_bytes) arrive as decimal strings — wrap
 *     in Number() at point of display, not here, to avoid silent rounding
 *     on the wire.
 *
 * State enums + label maps mirror api.proto's SkillVersionState and
 * SkillDeploymentStatusKind exactly.
 */
import { restGet, restPost, authedFetch, idForPath } from "./restClient.js";

// ── Enum labels ─────────────────────────────────────────────────

export const SKILL_VERSION_STATE = Object.freeze({
    UNSPECIFIED: "SKILL_VERSION_STATE_UNSPECIFIED",
    DRAFT:       "SKILL_VERSION_STATE_DRAFT",
    PUBLISHED:   "SKILL_VERSION_STATE_PUBLISHED",
    SUPERSEDED:  "SKILL_VERSION_STATE_SUPERSEDED",
    // Emergent versions captured from `skill_usage` (PR-1 migration adds
    // `observed` to `skill_versions.state`; proto `SKILL_VERSION_STATE_OBSERVED = 4`).
    // Without this entry an observed-state row falls through to "unknown".
    OBSERVED:    "SKILL_VERSION_STATE_OBSERVED",
});

const STATE_LABELS = {
    SKILL_VERSION_STATE_UNSPECIFIED: "unknown",
    SKILL_VERSION_STATE_DRAFT:       "draft",
    SKILL_VERSION_STATE_PUBLISHED:   "published",
    SKILL_VERSION_STATE_SUPERSEDED:  "superseded",
    SKILL_VERSION_STATE_OBSERVED:    "observed",
};

export function skillStateLabel(state) {
    return state ? STATE_LABELS[state] ?? "unknown" : "unknown";
}

const DEPLOY_STATUS_LABELS = {
    SKILL_DEPLOYMENT_STATUS_KIND_UNSPECIFIED:           "unknown",
    SKILL_DEPLOYMENT_STATUS_KIND_PENDING:               "pending",
    SKILL_DEPLOYMENT_STATUS_KIND_INSTALLED:             "installed",
    SKILL_DEPLOYMENT_STATUS_KIND_PARTIAL:               "partial",
    SKILL_DEPLOYMENT_STATUS_KIND_INSTALLED_NO_VENDORS:  "no vendors",
    SKILL_DEPLOYMENT_STATUS_KIND_REMOVED:               "removed",
    SKILL_DEPLOYMENT_STATUS_KIND_FAILED:                "failed",
};

export function skillDeploymentStatusLabel(kind) {
    return kind ? DEPLOY_STATUS_LABELS[kind] ?? "unknown" : "unknown";
}

// ── Observed-skill origin / vendor (events.v1 enums) ────────────
//
// The Observed surface (O-769) reads the ClickHouse `skill_usage` rollup,
// whose rows carry `events.v1.SkillOrigin` / `SkillVendor`. Wire values are
// the proto enum names; grpc-gateway emits/accepts them as strings.

export const SKILL_ORIGIN = Object.freeze({
    UNSPECIFIED:    "SKILL_ORIGIN_UNSPECIFIED",
    USER_LOCAL:     "SKILL_ORIGIN_USER_LOCAL",
    PROJECT_LOCAL:  "SKILL_ORIGIN_PROJECT_LOCAL",
    PLUGIN:         "SKILL_ORIGIN_PLUGIN",
    ORIGIN_MANAGED: "SKILL_ORIGIN_ORIGIN_MANAGED",
    BUILT_IN:       "SKILL_ORIGIN_BUILT_IN",
    UNKNOWN:        "SKILL_ORIGIN_UNKNOWN",
});

const ORIGIN_LABELS = {
    SKILL_ORIGIN_USER_LOCAL:     "User",
    SKILL_ORIGIN_PROJECT_LOCAL:  "Project",
    SKILL_ORIGIN_ORIGIN_MANAGED: "Origin",
    SKILL_ORIGIN_PLUGIN:         "Plugin",
    SKILL_ORIGIN_BUILT_IN:       "Built-in",
    SKILL_ORIGIN_UNSPECIFIED:    "Unknown",
    SKILL_ORIGIN_UNKNOWN:        "Unknown",
};

// Base label for an origin. For PLUGIN, callers that have the `pluginId`
// should append it (see OriginBadge); this returns just "Plugin".
export function skillOriginLabel(origin) {
    return origin ? ORIGIN_LABELS[origin] ?? "Unknown" : "Unknown";
}

export const SKILL_VENDOR = Object.freeze({
    UNSPECIFIED: "SKILL_VENDOR_UNSPECIFIED",
    CLAUDE:      "SKILL_VENDOR_CLAUDE",
    CURSOR:      "SKILL_VENDOR_CURSOR",
    CODEX:       "SKILL_VENDOR_CODEX",
});

const VENDOR_LABELS = {
    SKILL_VENDOR_CLAUDE: "Claude",
    SKILL_VENDOR_CURSOR: "Cursor",
    SKILL_VENDOR_CODEX:  "Codex",
    SKILL_VENDOR_UNSPECIFIED: "—",
};

export function skillVendorLabel(vendor) {
    return vendor ? VENDOR_LABELS[vendor] ?? "—" : "—";
}

// ── Upload format (api.v1.UploadFormat) ─────────────────────────
//
// Two-format upload (O-770): a `.skill`/`.zip` archive or a bare `SKILL.md`.
// The format pins the server-side validator path + size cap; it does NOT
// change the PUT Content-Type (the server signs every presign as
// `application/zip` regardless — see CreateSkillUploadUrl).

export const UPLOAD_FORMAT = Object.freeze({
    UNSPECIFIED:   "UPLOAD_FORMAT_UNSPECIFIED",
    SKILL_ARCHIVE: "UPLOAD_FORMAT_SKILL_ARCHIVE",
    SKILL_MD:      "UPLOAD_FORMAT_SKILL_MD",
});

// 1 MiB — mirrors shared_skill_format::MAX_SKILL_MD_BYTES (bare-md cap).
export const SKILL_MD_MAX_SIZE_BYTES = 1024 * 1024;

// Infer the wire format from a filename: `.skill`/`.zip` → archive,
// `.md` → bare SKILL.md, anything else → null (caller rejects the pick).
export function detectUploadFormat(name) {
    if (!name) return null;
    if (/\.(skill|zip)$/i.test(name)) return UPLOAD_FORMAT.SKILL_ARCHIVE;
    if (/\.md$/i.test(name)) return UPLOAD_FORMAT.SKILL_MD;
    return null;
}

// ── RPC wrappers ────────────────────────────────────────────────

export function listSkills() {
    return restGet("/v1/skills");
}

export function getSkill(skillId) {
    return restPost("/v1/skills:get", { skill_id: skillId });
}

// O-2413: per-(version × endpoint) install statuses for one skill, lifted off
// `getSkill` into a paginated RPC so a tenant with thousands of endpoints
// doesn't push a single GetSkill response past the gRPC response cap. Returns
// `{ deploymentStatuses, deployment_statuses, total, page, pageSize, page_size }`.
// `page` is zero-indexed; `pageSize` defaults to 100 server-side, max 500.
export function listSkillDeploymentStatuses(skillId, { page = 0, pageSize = SKILL_DEPLOYMENT_STATUSES_PAGE_SIZE } = {}) {
    return restPost("/v1/skills:list-deployment-statuses", {
        skill_id: skillId,
        page,
        page_size: pageSize,
    });
}

// Tightest safe size on the dashboard side. 500 rows × ~300 B/row ≈ 150 KiB —
// matches the backend's `LIST_SKILL_DEPLOYMENT_STATUSES_PAGE_SIZE_MAX` and
// keeps the rendered list bounded without a pager UI (most skills today have
// far fewer rows than this; a future PR can add Prev/Next if needed).
export const SKILL_DEPLOYMENT_STATUSES_PAGE_SIZE = 500;

// `format` (UploadFormat) pins the size cap + validator path on the server.
// Omitted/UNSPECIFIED defaults to SKILL_ARCHIVE server-side (V1 behavior).
export function createSkillUploadUrl({ sizeBytes, format }) {
    const body = { size_bytes: String(sizeBytes) };
    if (format) body.format = format;
    return restPost("/v1/skills:upload-url", body);
}

// Serialize the disambiguation directive into the proto `action` oneof.
//   undefined / { kind: "auto" }             → omit (server auto-resolves)
//   { kind: "create_new_skill" }             → { create_new_skill: {} } (sentinel)
//   { kind: "bump_version_of_skill_id", skillId } → { bump_version_of_skill_id }
// The empty-message sentinel (not a bool) is deliberate — see the
// FinalizeSkillUploadRequest.action doc comment (O-922 wire-vs-semantic).
function finalizeActionToWire(action) {
    if (!action || action.kind === "auto") return {};
    if (action.kind === "create_new_skill") return { create_new_skill: {} };
    if (action.kind === "bump_version_of_skill_id") {
        return { bump_version_of_skill_id: action.skillId };
    }
    return {};
}

// Returns FinalizeSkillUploadResponse: exactly one of `version` (success) or
// `disambiguationRequired` (chooser needed) is populated.
export function finalizeSkillUpload({ objectKey, tags, format, action }) {
    const body = {
        object_key: objectKey,
        tags: tags ?? [],
        ...finalizeActionToWire(action),
    };
    if (format) body.format = format;
    return restPost("/v1/skills:finalize-upload", body);
}

export function publishSkillVersion({ skillId, versionId }) {
    return restPost("/v1/skills:publish-version", {
        skill_id: skillId,
        version_id: versionId,
    });
}

export function archiveSkill(skillId) {
    return restPost("/v1/skills:archive", { skill_id: skillId });
}

export function unarchiveSkill(skillId) {
    return restPost("/v1/skills:unarchive", { skill_id: skillId });
}

export function deleteSkill(skillId) {
    return restPost("/v1/skills:delete", { skill_id: skillId });
}

// target = { kind: "all" } | { kind: "endpoints", endpointIds: [base64,...] }
function targetToWire(target) {
    if (target?.kind === "endpoints") {
        return { endpoint_set: { endpoint_ids: target.endpointIds ?? [] } };
    }
    return { all_endpoints: {} };
}

export function deploySkill({ skillId, versionId, target }) {
    return restPost("/v1/skills:deploy", {
        skill_id: skillId,
        version_id: versionId,
        target: targetToWire(target),
    });
}

export function undeploySkill({ skillId, target }) {
    return restPost("/v1/skills:undeploy", {
        skill_id: skillId,
        target: targetToWire(target),
    });
}

// Set or clear the operator override on a skill's display_disambiguator
// (admin only). Pass a non-empty string to set; pass null / "" to clear and
// re-engage server-side auto-derivation. The server rejects an empty-string
// value, so we coerce blank input to "clear" (omit the field). skill_id is a
// base64 `bytes` id riding in the path, so it goes through `idForPath`
// (base64url) — a raw `/` in the id would split the path and 404 at the gateway.
export function updateSkillDisplayDisambiguator(skillId, value) {
    const trimmed = typeof value === "string" ? value.trim() : "";
    const body = trimmed ? { value: trimmed } : {};
    return restPost(
        `/v1/skills/${idForPath(skillId)}:set-display-disambiguator`,
        body,
    );
}

// ── Observed skills (O-769) ─────────────────────────────────────
//
// Tenant-wide rollup of skills seen on endpoints (ClickHouse `skill_usage`).
// Admin only on the server. GET binds query params to proto field names.

export function listObservedSkills(opts = {}) {
    const p = new URLSearchParams();
    if (opts.page != null)     p.set("page", String(opts.page));
    if (opts.pageSize != null) p.set("page_size", String(opts.pageSize));
    if (opts.query)            p.set("query", opts.query);
    if (opts.origin)           p.set("origin", opts.origin);
    if (opts.vendor)           p.set("vendor", opts.vendor);
    if (opts.availableOnly)    p.set("available_only", "true");
    if (opts.contentSha256)    p.set("content_sha256", opts.contentSha256);
    const qs = p.toString();
    return restGet(`/v1/skills/observed${qs ? `?${qs}` : ""}`);
}

// Per-endpoint skill inventory (disk path + SKILL.md preview). Admin only.
export function listEndpointSkillUsage({ endpointId, page, pageSize, origin, vendor }) {
    const body = { endpoint_id: endpointId };
    if (page != null)     body.page = page;
    if (pageSize != null) body.page_size = pageSize;
    if (origin)           body.origin = origin;
    if (vendor)           body.vendor = vendor;
    return restPost("/v1/endpoints:list-skill-usage", body);
}

// ── Presigned blob upload ───────────────────────────────────────
//
// Direct PUT to the presigned URL minted by createSkillUploadUrl. No
// auth header (the URL is signed). The backend signs with
// Content-Type: application/zip — the browser would otherwise omit
// Content-Type for an unrecognized `.skill` extension and break the
// signature at MinIO/S3. Uses XMLHttpRequest because fetch() still
// can't surface upload progress in any browser as of 2026.

export function uploadSkillBlob(uploadUrl, blob, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader("Content-Type", "application/zip");
        if (onProgress && xhr.upload) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) onProgress(e.loaded, e.total);
            };
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Skill upload failed: ${xhr.status} ${xhr.statusText}`));
        };
        xhr.onerror   = () => reject(new Error("Skill upload network error"));
        xhr.ontimeout = () => reject(new Error("Skill upload timed out"));
        xhr.send(blob);
    });
}

// 50 MiB — matches the per-tenant ceiling enforced by the gateway's
// finalize-upload handler. Surface this in the upload UI's client-side
// check so the user sees the limit before a 50MB PUT.
export const SKILL_MAX_SIZE_BYTES = 50 * 1024 * 1024;
